'use client';

import { Alert, Icon, Select } from '../../components';
import { parseDefaultCheckinTime } from '@fshr/shared';
import { ArrivalTime } from '@fshr/shared';
import { cn } from '@fshr/shared-react';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';

type ArrivalTimeProps = {
	t: (text: string, params?: (string | number)[]) => string;
	arrivalTime: ArrivalTime | undefined;
	registerField: any;
	checkinDate?: string;
	hour: string;
	minute: string;
	ampm: string;
	defaultCheckinTime?: string;
	description?: string;
	hideHeading?: boolean;
	descriptionClassName?: string;
	descriptionClassName1?: string;
};

export function ArrivalTimeComponent(props: ArrivalTimeProps) {
	const {
		t,
		arrivalTime,
		registerField,
		checkinDate,
		hour,
		minute,
		ampm,
		defaultCheckinTime,
		description,
		hideHeading,
		descriptionClassName,
		descriptionClassName1,
	} = props;
	const hours = Array.from({ length: 12 }, (_, i) => i + 1);
	const minutes = ['00', '15', '30', '45'];
	const periods = ['AM', 'PM'];
	const [showEarlierTimeAlert, setShowEarlierTimeAlert] = useState(false);

	const defaultArrivalTime = arrivalTime || {
		...(parseDefaultCheckinTime(defaultCheckinTime) || { hour: '3', minute: '0', ampm: 'pm' }),
		display: defaultCheckinTime || '3:00 PM',
	};

	// Helper to convert to ISO string
	function convertToISODate(hour: string, minute: string, ampm: string) {
		let hourNum = parseInt(hour);
		if (ampm === 'PM' && hourNum !== 12) hourNum += 12;
		if (ampm === 'AM' && hourNum === 12) hourNum = 0;
		const hourStr = hourNum.toString().padStart(2, '0');
		const minuteStr = minute.padStart(2, '0');
		return `${checkinDate ?? format(new Date().toISOString(), 'yyyy-MM-dd')}T${hourStr}:${minuteStr}:00`;
	}

	// Compare selected time to official check-in time
	useEffect(() => {
		if (!defaultCheckinTime || !hour || !minute || !ampm) {
			setShowEarlierTimeAlert(false);
			return;
		}

		const officialTime = parseDefaultCheckinTime(defaultCheckinTime);
		if (!officialTime) {
			setShowEarlierTimeAlert(false);
			return;
		}

		const officialISO = convertToISODate(officialTime.hour, officialTime.minute, officialTime.ampm);
		const selectedISO = convertToISODate(hour, minute, ampm);
		setShowEarlierTimeAlert(new Date(selectedISO) < new Date(officialISO));
	}, [hour, minute, ampm, defaultCheckinTime, checkinDate]);

	return (
		<>
			{!hideHeading && (
				<div className="flex">
					<h3 className="text-subtitle1">{t('Arrival Time')}</h3>
				</div>
			)}
			<div className={cn('flex flex-col', descriptionClassName)}>
				<div className={cn('flex', descriptionClassName1)}>
					<p className="text-body2">
						{description ?? t('To prepare your room, what is your estimated time of arrival?')}
					</p>
				</div>
				{showEarlierTimeAlert && (
					<Alert intent="info" kind="subtle" icon={<Icon icon="information" />}>
						{t(
							'Please note your requested time is before our official Check-In time.  We will do our best to accommodate your request.'
						)}
					</Alert>
				)}
				<div className="flex w-full spacing-inline-m">
					<div className="flex-1 flex flex-col">
						<Select
							id="Hour"
							label={t('Hour')}
							className="text-body2 flex items-center w-full gap-100 h-[47px] bg-surface-form"
							options={hours.map(hour => ({
								label: `${hour}`.padStart(2, '0'),
								value: hour,
								className: 'text-label text-text-secondary',
							}))}
							{...registerField({
								name: 'hour',
								initialValue: arrivalTime?.hour || defaultArrivalTime.hour,
								options: { required: false },
							})}
						/>
					</div>
					<div className="flex-1 flex flex-col">
						<Select
							id="Min"
							label={t('Min')}
							className="text-body2 flex items-center w-full gap-100 h-[47px] bg-surface-form"
							options={minutes.map(minute => ({
								label: `${minute}`,
								value: minute,
								className: 'text-label text-text-secondary',
							}))}
							{...registerField({
								name: 'minutes',
								initialValue: arrivalTime?.minute || defaultArrivalTime.minute,
								options: { required: false },
							})}
						/>
					</div>
					<div className="flex-1 flex flex-col">
						<Select
							id="ampm"
							label={t('PM/AM')}
							className="text-body2 flex items-center w-full gap-100 h-[47px] bg-surface-form"
							options={periods.map(ampm => ({
								label: `${ampm}`,
								value: ampm,
								className: 'text-label text-text-secondary',
							}))}
							{...registerField({
								name: 'ampm',
								initialValue: arrivalTime?.ampm || defaultArrivalTime.ampm,
								options: { required: false },
							})}
						/>
					</div>
				</div>
			</div>
		</>
	);
}
